// src/pages/Dashboard.jsx
import React, { useEffect, useState, useMemo } from "react";
import Layout from "../components/Layout";
import api from "../services/api";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  BarChart, Bar, Legend,
} from "recharts";

export default function Dashboard() {
  const [usersData, setUsersData] = useState([]);
  const [paymentsData, setPaymentsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ Date filter (no UI change visible)
  const [dateRange, setDateRange] = useState("all"); // all | 7 | 30

  const role = localStorage.getItem("userRole");

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      setError("");
      try {
        const [usersRes, paymentsRes] = await Promise.all([
          api.get("/analytics/users"),
          api.get("/analytics/payments"),
        ]);
        setUsersData(usersRes.data || []);
        setPaymentsData(paymentsRes.data || []);
      } catch (err) {
        console.error("Analytics error:", err);
        setError("Could not load analytics.");
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  // ✅ FILTER PAYMENTS (date wise)
  const filteredPayments = useMemo(() => {
    if (dateRange === "all") return paymentsData;

    const days = Number(dateRange);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);

    return paymentsData.filter(p => {
      if (!p.created_at) return false;
      return new Date(p.created_at) >= cutoff;
    });
  }, [paymentsData, dateRange]);

  // ✅ FIXED TOTAL (no string bug)
  const totalPayments = filteredPayments.reduce(
    (s, p) => s + Number(p.amount || 0),
    0
  );

  // ✅ REVENUE CHART FIX (group by month)
  const revenueData = useMemo(() => {
    const map = {};

    filteredPayments.forEach(p => {
      if (!p.created_at) return;

      const d = new Date(p.created_at);
      const key = `${d.getFullYear()}-${d.getMonth() + 1}`;

      if (!map[key]) {
        map[key] = { month: key, amount: 0 };
      }

      map[key].amount += Number(p.amount || 0);
    });

    return Object.values(map).sort((a, b) =>
      new Date(a.month) - new Date(b.month)
    );
  }, [filteredPayments]);

  return (
    <Layout>
      <div className="w-full max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-indigo-600">Dashboard</h1>

          {role === "admin" && (
            <button
              onClick={() => window.location.href = "/create-request"}
              className="bg-indigo-600 text-white px-4 py-2 rounded"
            >
              Create Repair Request
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* ✅ DATE FILTER (logic only, no UI styling change) */}
        <div className="mb-4">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="border p-2 rounded"
          >
            <option value="all">All Time</option>
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500">Monthly Active Users</div>
            <div className="text-2xl font-semibold mt-2">
              {usersData.reduce((s, x) => s + (x.count || 0), 0)}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500">Payments (Total)</div>
            <div className="text-2xl font-semibold mt-2">
              ₹ {totalPayments.toFixed(2)}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-500">Active Users This Month</div>
            <div className="text-2xl font-semibold mt-2">
              {usersData.length > 0
                ? usersData[usersData.length - 1].count
                : 0}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-xl shadow">
            <h2 className="text-lg font-semibold mb-4">
              Monthly Active Users
            </h2>
            <div className="w-full h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={usersData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#6366F1"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow">
            <h2 className="text-lg font-semibold mb-4">
              Payments Over Time
            </h2>
            <div className="w-full h-72">
              <ResponsiveContainer width="100%" height="100%">
                {/* ✅ FIXED REVENUE CHART */}
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="amount" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {loading && (
          <div className="mt-6 text-center text-gray-500">
            Loading analytics…
          </div>
        )}
      </div>
    </Layout>
  );
}